import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  actionPaletteCommands,
  filterPaletteCommands,
  groupPaletteCommands,
  type ActionPaletteCommand,
} from './actionPaletteModel';
import type { AssigneeDirectory } from './actionEditModel';
import { useHwcgFlag } from 'utils/featureFlags';

type Props = {
  open: boolean;
  onClose: () => void;
  directory: AssigneeDirectory;
  canHandover: boolean;
  onRun: (command: ActionPaletteCommand) => void;
};

/**
 * A searchable list of writes against the selected tasks.
 *
 * Modelled on the DisasterGPT slash menu rather than cmdk: search at the top,
 * grouped results, arrow keys over the visible rows. The catalogue itself lives
 * in `actionPaletteCommands` so this file is just the keyboard and the list.
 */
export const ActionCommandPalette: React.FC<Props> = ({
  open,
  onClose,
  directory,
  canHandover,
  onRun,
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);
  if (open && !wasOpen.current) {
    wasOpen.current = true;
    if (search) setSearch('');
    if (activeIndex !== 0) setActiveIndex(0);
  } else if (!open && wasOpen.current) {
    wasOpen.current = false;
  }

  const hwcg = useHwcgFlag();
  const commands = useMemo(
    () => actionPaletteCommands({ directory, canHandover, hwcg }),
    [canHandover, directory, hwcg]
  );
  const visible = useMemo(
    () => filterPaletteCommands(commands, search),
    [commands, search]
  );
  const groups = useMemo(() => groupPaletteCommands(visible), [visible]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const run = (command: ActionPaletteCommand) => {
    onRun(command);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, visible.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const command = visible[activeIndex];
      if (command) run(command);
    }
  };

  let cursor = -1;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="dark tw:max-w-lg tw:gap-2 tw:p-3"
        showCloseButton={false}
        aria-label="Bulk update"
        onKeyDown={onKeyDown}
      >
        <DialogTitle className="tw:sr-only">Bulk update</DialogTitle>
        <Input
          ref={inputRef}
          placeholder="Assign, change status, switch view…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setActiveIndex(0);
          }}
          aria-label="Search commands"
        />
        <div className="tw:max-h-[50vh] tw:overflow-y-auto">
          {groups.length === 0 ? (
            <div className="tw:p-2 tw:text-sm tw:text-foreground">
              No matching commands
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.group} className="tw:mb-2">
                <div className="tw:px-2 tw:pt-2 tw:text-xs tw:text-foreground">
                  {group.group}
                </div>
                {group.commands.map((command) => {
                  cursor += 1;
                  const index = cursor;
                  const active = index === activeIndex;
                  return (
                    <Button
                      key={command.id}
                      type="button"
                      ref={active ? activeRef : undefined}
                      variant={active ? 'secondary' : 'ghost'}
                      className="tw:w-full tw:justify-start"
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => run(command)}
                    >
                      {command.label}
                    </Button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActionCommandPalette;
